import { useCallback, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { WorkbookInstance } from '@fortune-sheet/react';
import { commitEditThen, getInternals } from './excel/workbookInternals';

export interface UseSheetCloseFlowArgs {
    onClose: () => void;
    workbookRef: RefObject<WorkbookInstance>;
    /** Awaited flush of a pending DRAFT autosave — see
     *  useSheetEditorDrawerLogic's own doc comment. */
    flushPendingAutosave: () => Promise<boolean>;
    /** "Close without saving" (the save-failed dialog) — see
     *  useSheetEditorDrawerLogic's own doc comment. */
    discardPendingChanges: () => void;
}

export interface UseSheetCloseFlowReturn {
    /** A failed close-flush opens the save-failed dialog instead of leaving the
     *  drawer silently open. */
    closeFailedOpen: boolean;
    setCloseFailedOpen: (open: boolean) => void;
    /** ×/backdrop/idle-Esc: commits any in-progress cell edit, then flushes
     *  the pending autosave before calling `onClose`. */
    commitEditAndClose: () => void;
    /** The save-failed dialog's Retry — re-runs the flush directly (no edit to
     *  commit first; the dialog is already modal over the grid). */
    handleRetryCloseFailed: () => void;
    /** The save-failed dialog's "Close without saving" — the only path that ever
     *  discards unsaved content, never automatic. */
    handleCloseWithoutSaving: () => void;
}

/**
 * Close/flush/retry orchestration for SheetEditorDrawer: every non-cancel
 * close (×, backdrop, idle Esc) commits any in-progress cell edit,
 * then AWAITS flushPendingAutosave BEFORE `onClose` — a failed flush opens
 * the save-failed dialog (Retry re-runs the flush; "Close without saving" discards
 * and closes) instead of leaving the drawer silently open. `closingRef` guards
 * a second × arriving while a flush (initial or Retry) is still pending.
 */
export const useSheetCloseFlow = ({
    onClose,
    workbookRef,
    flushPendingAutosave,
    discardPendingChanges,
}: UseSheetCloseFlowArgs): UseSheetCloseFlowReturn => {
    const [closeFailedOpen, setCloseFailedOpen] = useState(false);

    // Every close/save path here is NOT an Esc-cancel, so it must commit any
    // in-progress cell edit first (same mechanism saveShortcut's Ctrl+S
    // uses) — otherwise typed-but-uncommitted text is silently dropped. Esc
    // itself is untouched (rule 3 already cancels via the library's own Esc
    // handling before ever reaching onClose).
    const commitEditThenRun = useCallback((then: () => void) => commitEditThen(getInternals(workbookRef.current), then), [workbookRef]);

    const closingRef = useRef(false);
    // Shared by the initial close attempt and Retry: awaits the flush,
    // closing the dialog and the drawer on success; runs `onFail` otherwise
    // (opens the dialog for the initial attempt, a no-op for Retry — it's
    // already open).
    const attemptFlushAndClose = useCallback(
        async (onFail: () => void) => {
            if (closingRef.current) return;
            closingRef.current = true;
            try {
                if (await flushPendingAutosave()) {
                    setCloseFailedOpen(false);
                    onClose();
                } else {
                    onFail();
                }
            } catch (err) {
                // flushPendingAutosave is expected to resolve false on a
                // genuine failure, never throw — but an unexpected throw
                // (a defensive backstop for any of its own dependencies,
                // e.g. a still-rejecting pendingDocIdPromise) must land here
                // too, not escape as an unhandled rejection that leaves the
                // drawer open with no feedback at all.
                console.error('[useSheetCloseFlow] flush threw unexpectedly', err);
                onFail();
            } finally {
                closingRef.current = false;
            }
        },
        [flushPendingAutosave, onClose],
    );

    const commitEditAndClose = useCallback(
        () => commitEditThenRun(() => attemptFlushAndClose(() => setCloseFailedOpen(true))),
        [commitEditThenRun, attemptFlushAndClose],
    );

    const handleRetryCloseFailed = useCallback(() => attemptFlushAndClose(() => {}), [attemptFlushAndClose]);

    const handleCloseWithoutSaving = useCallback(() => {
        discardPendingChanges();
        setCloseFailedOpen(false);
        onClose();
    }, [discardPendingChanges, onClose]);

    return { closeFailedOpen, setCloseFailedOpen, commitEditAndClose, handleRetryCloseFailed, handleCloseWithoutSaving };
};
